import { useEffect, useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import type { MemberStatement, Projection, SchemeParams } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatCard } from '../../ui/components';

export function MyContributions() {
  const statementQ = useApiQuery<MemberStatement>('/api/v1/members/me/statement');
  const paramsQ = useApiQuery<SchemeParams>('/api/v1/benefits/parameters');
  const memberNotFound = statementQ.error?.toLowerCase().includes('not found') ?? false;

  return (
    <div>
      <PageHeader title="My Contributions" subtitle="Your posted contribution history, year by year" />

      {statementQ.loading ? (
        <Spinner />
      ) : memberNotFound ? (
        <EmptyState
          title="No pension record yet"
          hint="Your account is created automatically the first time your employer posts a contribution for you."
        />
      ) : statementQ.error ? (
        <ErrorNote message={statementQ.error} />
      ) : statementQ.data ? (
        <>
          <div className="mb-6 grid gap-3 sm:grid-cols-3">
            <StatCard label="Total contributed" value={scr(statementQ.data.totals.totalContrib)} />
            <StatCard label="Years of contribution" value={statementQ.data.totals.yearsOfContribution} />
            <StatCard label="Contribution years on record" value={statementQ.data.years.length} />
          </div>

          <div className="card mb-6 overflow-x-auto">
            {statementQ.data.years.length === 0 ? (
              <EmptyState title="No contribution years posted yet" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Year</th>
                    <th className="table-head text-right">Months contributed</th>
                    <th className="table-head text-right">Gross wages</th>
                    <th className="table-head text-right">Total contributed (5%+5%)</th>
                  </tr>
                </thead>
                <tbody>
                  {[...statementQ.data.years].reverse().map((y) => (
                    <tr key={y.year} className="hover:bg-slate-50">
                      <td className="table-cell font-medium">{y.year}</td>
                      <td className="table-cell text-right">{y.months}</td>
                      <td className="table-cell text-right tabular-nums">{scr(y.totalWages)}</td>
                      <td className="table-cell text-right tabular-nums">{scr(y.totalContrib)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <ProjectionCalculator memberId={statementQ.data.member.id} defaults={paramsQ.data} />
        </>
      ) : null}
    </div>
  );
}

function ProjectionCalculator({ memberId, defaults }: { memberId: string; defaults: SchemeParams | null }) {
  const [accrualPct, setAccrualPct] = useState('1.5');
  const [refYears, setRefYears] = useState('5');
  const [serviceYears, setServiceYears] = useState('');
  const [result, setResult] = useState<Projection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (defaults) {
      setAccrualPct(String(defaults.ACCRUAL_RATE_PCT));
      setRefYears(String(defaults.REFERENCE_YEARS));
    }
  }, [defaults]);

  const run = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const q = new URLSearchParams({ accrualPct, refYears });
      if (serviceYears) q.set('serviceYears', serviceYears);
      const r = await api.get<Projection>(`/api/v1/members/${memberId}/projection?${q.toString()}`);
      setResult(r);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not calculate a projection');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Projected monthly pension</h2>
      <p className="mb-3 text-xs text-slate-500">
        A planning estimate only — the authoritative calculation runs when you submit and an officer processes a
        benefit claim.
      </p>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="grid grid-cols-3 gap-3">
        <Field label="Accrual rate %">
          <input className="field-input" type="number" step="0.1" value={accrualPct} onChange={(e) => setAccrualPct(e.target.value)} />
        </Field>
        <Field label="Reference years">
          <input className="field-input" type="number" step="1" value={refYears} onChange={(e) => setRefYears(e.target.value)} />
        </Field>
        <Field label="Project to years of service (optional)">
          <input
            className="field-input"
            type="number"
            step="1"
            value={serviceYears}
            onChange={(e) => setServiceYears(e.target.value)}
            placeholder="Uses your current total"
          />
        </Field>
      </div>
      <button className="btn-primary mt-3" onClick={run} disabled={busy}>
        {busy ? 'Calculating…' : 'Calculate projection'}
      </button>
      {result ? (
        <div className="mt-4 rounded-lg bg-brand-50 px-4 py-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-brand-700">Projected monthly pension</div>
          <div className="text-2xl font-bold tabular-nums text-brand-700">{scr(result.monthlyPension)}</div>
          <div className="mt-1 text-xs text-slate-500">
            Based on avg monthly wage {scr(result.inputs.avgMonthlyWage)} × {result.inputs.accrualPct}% ×{' '}
            {result.inputs.serviceYears} years of service
          </div>
        </div>
      ) : null}
    </div>
  );
}
